import type { WorkbookDomain } from '@mathflat/domain/@entities/(Content)/Workbook/domain.ts'
import type { Entity as WorkbookEntity } from '@mathflat/domain/@entities/(Content)/Workbook/dto.ts'
import { CURRICULUM_KEYS } from '@mathflat/domain/@entities/Curriculum/constant'
import type { Entity as CurriculumEntity } from '@mathflat/domain/@entities/Curriculum/dto.ts'
import type { ValueOf } from '@mathflat/shared/@types/utilityTypes'
import { makeAutoObservable, runInAction, toJS } from 'mobx'

import { workbookApi } from '~/@common/api/workbookApi.ts'
import { isWorkbookTypeAllowed } from '~/@common/models/(Permission)/permissionUtils.ts'
import { commonRepo } from '~/@common/services/repo.service.ts'

export type SearchWorkbookType = ValueOf<
  Pick<typeof WorkbookDomain.TYPE, '커스텀시그니처교재' | '학원그룹교재' | '시중교재' | '교과서'>
>

const WORKBOOK_TYPES: SearchWorkbookType[] = [
  'CUSTOM_SIGNATURE',
  'ACADEMY_GROUP',
  'PUBLIC',
  'SCHOOL',
]

const ASSIGN_SEARCH_TYPE_BY_WORKBOOK = {
  CUSTOM_SIGNATURE: 'ONLY_ASSIGNED',
  ACADEMY_GROUP: 'ONLY_ASSIGNED',
  PUBLIC: 'WITH_ASSIGNED',
  SCHOOL: 'WITH_ASSIGNED',
} as const

const WORKBOOK_TYPE_KR = {
  CUSTOM_SIGNATURE: '시그니처',
  ACADEMY_GROUP: '학원그룹교재',
  PUBLIC: '시중교재',
  SCHOOL: '교과서',
} as const

const MAX_SIZE_BY_WORKBOOK_TYPE = {
  CUSTOM_SIGNATURE: 5,
  ACADEMY_GROUP: 5,
  PUBLIC: 5,
  SCHOOL: 3,
}

export type RequiredWorkbookInfo = Pick<WorkbookEntity.Workbook, 'id' | 'type' | 'fulltitle'>

type WorkbookId = number
const defaultCheckedWorkbookColl = {
  CUSTOM_SIGNATURE: new Map<WorkbookId, RequiredWorkbookInfo>(),
  PUBLIC: new Map<WorkbookId, RequiredWorkbookInfo>(),
  SCHOOL: new Map<WorkbookId, RequiredWorkbookInfo>(),
  ACADEMY_GROUP: new Map<WorkbookId, RequiredWorkbookInfo>(),
} as const

export class WorkbookSearchService {
  static WORKBOOK_TYPES = WORKBOOK_TYPES
  static WORKBOOK_TYPE_KR = WORKBOOK_TYPE_KR
  static MAX_SIZE_BY_WORKBOOK_TYPE = MAX_SIZE_BY_WORKBOOK_TYPE

  _isChanged = false

  searchText = ''
  _initCurriculumKey: string | null = null
  curriculumKey: string | null = null

  private checkWorkbookColl = toJS(defaultCheckedWorkbookColl)

  selectedWorkbookType: SearchWorkbookType = 'CUSTOM_SIGNATURE'

  searchWorkbookMap = new Map<SearchWorkbookType, Map<WorkbookId, WorkbookEntity.Workbook>>()

  get allowedWorkbookTypes(): SearchWorkbookType[] {
    return WORKBOOK_TYPES.filter((type) => isWorkbookTypeAllowed(commonRepo.permission, type))
  }

  /**
   * 현재 selectedWorkbookType 이 허용되는지 검사 후 아니면 첫 허용 타입으로 정규화.
   * fetchWorkbookForTypes 호출 전 또는 권한 hydrate 후에 호출해서 빈 탭으로 떨어지지 않게 한다.
   */
  normalizeSelectedWorkbookType() {
    const allowed = this.allowedWorkbookTypes
    if (!allowed.includes(this.selectedWorkbookType) && allowed[0]) {
      this.selectedWorkbookType = allowed[0]
    }
  }

  get checkedWorkbookToArray() {
    return [...Object.values(this.checkWorkbookColl)]
  }

  getCheckedWorkbookSizeBy(workbookType: SearchWorkbookType) {
    return this.checkWorkbookColl[workbookType].size
  }

  checkIsMaxSizeReached(workbookType: SearchWorkbookType) {
    return (
      this.getCheckedWorkbookSizeBy(workbookType) >=
      WorkbookSearchService.MAX_SIZE_BY_WORKBOOK_TYPE[workbookType]
    )
  }

  changed() {
    this._isChanged = true
  }

  unchanged() {
    this._isChanged = false
  }

  get isChanged() {
    return this._isChanged
  }

  setSelectWorkbookType(type: SearchWorkbookType) {
    this.selectedWorkbookType = type
  }

  clear() {
    this.checkWorkbookColl = toJS(defaultCheckedWorkbookColl)
    if (this._initCurriculumKey && this._initCurriculumKey !== this.curriculumKey) {
      this.setCurriculumKey(this._initCurriculumKey)
    }
    this._isChanged = true
  }

  getFilteredWorkbookList(type: SearchWorkbookType) {
    const workbookData = this.searchWorkbookMap.get(type)
    if (!workbookData) {
      return
    }
    return (
      [...workbookData.values()].filter(({ fulltitle }) =>
        fulltitle
          .replaceAll(' ', '')
          .toLowerCase()
          .includes(this.searchText.replaceAll(' ', '').toLowerCase()),
      ) ?? []
    )
  }

  get hasFilteredWorkbooks() {
    return Boolean(
      this.allowedWorkbookTypes.find((type) => this.getFilteredWorkbookList(type)?.length),
    )
  }

  get selectedWorkbookList() {
    const selectedWorkbooks = this.searchWorkbookMap.get(this.selectedWorkbookType)
    if (!selectedWorkbooks) return []
    return [...selectedWorkbooks.values()]
  }

  checkWorkbook(type: SearchWorkbookType, workbook: WorkbookEntity.Workbook) {
    if (this.checkWorkbookColl[type].has(workbook.id)) return true

    return !(this.checkWorkbookColl[type].size >= MAX_SIZE_BY_WORKBOOK_TYPE[type])
  }

  toggleWorkbook(type: SearchWorkbookType, workbook: WorkbookEntity.Workbook) {
    if (!this._isChanged) this.changed()
    const checkWorkbookColl = this.checkWorkbookColl[type]
    if (checkWorkbookColl.has(workbook.id)) {
      checkWorkbookColl.delete(workbook.id)
    } else {
      checkWorkbookColl.set(workbook.id, workbook)
    }
  }

  setSearchText = (text: string) => {
    this.searchText = text
  }
  setCurriculumKey(curriculumKey: string) {
    if (this.curriculumKey === curriculumKey) return
    if (!this._isChanged) this.changed()

    this.curriculumKey = curriculumKey

    this.checkWorkbookColl = toJS(defaultCheckedWorkbookColl)

    this.fetchWorkbookForTypes()
  }

  // 교과서 커리큘럼키 계산
  getCurriculumKeyForSchoolbook = (curriculumKey: CurriculumEntity.TrieKey) => {
    // 교과서의 경우 특정 학년에서 curriculumKey를 그대로 내려보내면 안되기에 mapping하는 로직
    // 특정 학년의 교과서는 2학기를 더 이상 지원하지 않기 때문. (1개의 교과서를 1년동안 사용)
    const curriculumInfo = commonRepo.curriculumCore?.getItemByTrieKey(curriculumKey)

    if (!curriculumInfo) return curriculumKey

    const { schoolType, trieKey, upperTrieKey } = curriculumInfo

    // 중학교 교과서는 학기 구분이 없기 때문에 학기 curriculumKey가 아닌 학년 curriculumKey를 보내야 함
    if (schoolType === 'MIDDLE') return upperTrieKey
    // 고등수학(하) 는 (상)으로 보내야함
    if (trieKey === CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_수학_하)
      return CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_수학_상

    return curriculumKey
  }

  fetchWorkbookForTypes() {
    if (!this.curriculumKey) return
    // 차단된 타입이 selectedWorkbookType 으로 남아 빈 탭이 되는 걸 방지
    this.normalizeSelectedWorkbookType()
    for (const type of this.allowedWorkbookTypes) {
      const curriculumKey =
        type === 'SCHOOL'
          ? this.getCurriculumKeyForSchoolbook(this.curriculumKey)
          : this.curriculumKey

      if (!curriculumKey) continue

      WorkbookSearchService.fetchWorkbooks(type, curriculumKey)?.then((workbooks) => {
        runInAction(() =>
          this.searchWorkbookMap.set(
            type,
            new Map(workbooks.map((workbook) => [workbook.id, workbook])),
          ),
        )
      })
    }
  }

  static fetchWorkbooks(workbookType: SearchWorkbookType, curriculumKey: string) {
    if (commonRepo.studentId) {
      return workbookApi.getWorkbooks<SearchWorkbookType, 'STUDENT'>({
        curriculumKey,
        type: workbookType,
        assignStudentId: commonRepo.studentId,
        assignSearchType: ASSIGN_SEARCH_TYPE_BY_WORKBOOK[workbookType],
        assignType: 'STUDENT',
        size: 6000,
      })
    }
  }

  init({
    curriculumKey,
    selectedWorkbookType,
    searchText,
    selectedWorkbooks,
  }: {
    curriculumKey: string
    searchText: string
    selectedWorkbookType: SearchWorkbookType
    selectedWorkbooks: RequiredWorkbookInfo[]
  }) {
    this.searchText = searchText
    this.selectedWorkbookType = selectedWorkbookType
    this._initCurriculumKey = this.curriculumKey = curriculumKey

    this.checkWorkbookColl = toJS(defaultCheckedWorkbookColl)

    for (const workbook of selectedWorkbooks) {
      const { type, id } = workbook
      this.checkWorkbookColl[type].set(id, workbook)
    }
    // 외부에서 전달된 selectedWorkbookType 이 차단된 타입이면 첫 허용 타입으로 정규화
    this.normalizeSelectedWorkbookType()
  }

  constructor() {
    makeAutoObservable(this)
  }
}
